<template>
  <div class="app-change-personal-data">
    <form @submit.prevent="saveData()" class="change-personal-data-form content-limiter">
      <!-- Persönliche Daten -->
      <fieldset class="inputs-container">
        <legend class="visually-hidden">Persönliche Daten</legend>
        <!-- Anrede -->
        <div class="input-field">
          <label class="text-base-medium" for="firstName"> Anrede<span class="required" aria-hidden="true">*</span> </label>
          <div class="radio-group">
            <label>
              <input
                type="radio"
                name="gender"
                value="Frau"
                v-model="currentCustomer.salutation"
                required
                aria-describedby="gender-hint"
              />
              <span class="custom-radio"></span>
              <span class="text-sm">Frau</span>
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="Herr"
                v-model="currentCustomer.salutation"
                required
                aria-describedby="gender-hint"
              />
              <span class="custom-radio"></span>
              <span class="text-sm">Herr</span>
            </label>
          </div>
          <small id="gender-hint" class="sr-only">Pflichtfeld</small>
          <div
            id="gender-error"
            class="error-feedback"
            v-if="!isValid('salutation', 'currentCustomer') && $v.currentCustomer.salutation.$dirty"
          >
            <span v-if="$v.currentCustomer.salutation.required === false"> Dieses Feld ist ein Pflichtfeld<br /> </span>
          </div>
        </div>
        <!-- Vorname -->
        <div class="input-field">
          <label class="text-base-medium" for="firstName"> Vorname<span class="required" aria-hidden="true">*</span> </label>
          <p class="text-base" id="firstName">{{ currentCustomer.firstName }}</p>
        </div>
        <!-- Nachname -->
        <div class="input-field">
          <label class="text-base-medium" for="lastName"> Nachname<span class="required" aria-hidden="true">*</span> </label>
          <p id="lastName" class="text-base">{{ currentCustomer.lastName }}</p>
        </div>
      </fieldset>

      <!-- Adressdaten -->
      <fieldset class="inputs-container">
        <legend class="text-xl visually-hidden">Adressdaten</legend>
        <!-- Straße & Hausnummer -->
        <div class="input-group">
          <div class="input-field input-field--fixed">
            <label class="text-base-medium" for="street"> Straße<span class="required" aria-hidden="true">*</span> </label>
            <ValidationIndicator
              :dirty="$v.currentCustomer.address.street.$dirty"
              :valid="isValid('street', 'address', 'currentCustomer')"
            >
              <input
                id="street"
                name="street"
                placeholder="Straße"
                type="text"
                class="input"
                v-model="currentCustomer.address.street"
                required
                @blur="$v.currentCustomer.address.street.$touch"
                @click="$v.currentCustomer.address.street.$reset"
                :class="{
                  'input--invalid':
                    $v.currentCustomer.address.street.$dirty && !isValid('street', 'address', 'currentCustomer'),
                  'input--valid': $v.currentCustomer.address.street.$dirty && isValid('street', 'address', 'currentCustomer')
                }"
                aria-describedby="street-hint"
                aria-errormessage="street-error"
              />
            </ValidationIndicator>
            <small id="street-hint" class="sr-only">Pflichtfeld</small>
            <div
              id="street-error"
              class="error-feedback"
              v-if="!isValid('street', 'address', 'currentCustomer') && $v.currentCustomer.address.street.$dirty"
            >
              <span v-if="$v.currentCustomer.address.street.required === false"> Bitte gib deine Straße ein.<br /> </span>
            </div>
          </div>

          <div class="input-field input-field--fluid">
            <label class="text-base-medium" for="houseNumber">
              Hausnummer<span class="required" aria-hidden="true">*</span>
            </label>
            <ValidationIndicator
              :dirty="$v.currentCustomer.address.houseNumber.$dirty"
              :valid="isValid('houseNumber', 'address', 'currentCustomer')"
            >
              <input
                id="houseNumber"
                name="houseNumber"
                placeholder="Hausnummer"
                type="text"
                class="input"
                v-model="currentCustomer.address.houseNumber"
                required
                @blur="$v.currentCustomer.address.houseNumber.$touch"
                @click="$v.currentCustomer.address.houseNumber.$reset"
                :class="{
                  'input--invalid':
                    $v.currentCustomer.address.houseNumber.$dirty && !isValid('houseNumber', 'address', 'currentCustomer'),
                  'input--valid':
                    $v.currentCustomer.address.houseNumber.$dirty && isValid('houseNumber', 'address', 'currentCustomer')
                }"
                aria-describedby="houseNumber-hint"
              />
            </ValidationIndicator>
            <small id="houseNumber-hint" class="sr-only">Pflichtfeld</small>
            <div
              id="houseNumber-error"
              class="error-feedback"
              v-if="!isValid('houseNumber', 'address', 'currentCustomer') && $v.currentCustomer.address.houseNumber.$dirty"
            >
              <span v-if="$v.currentCustomer.address.houseNumber.required === false">
                Bitte gib deine Hausnummer ein.“<br />
              </span>
              <span v-if="$v.currentCustomer.address.houseNumber.alphaNumeric === false">
                Deine Haunsummer darf nur Buchstaben und Zahlen beinhalten<br />
              </span>
              <span v-if="$v.currentCustomer.address.houseNumber.maxLength === false">
                Deine Hausnummer darf maximal neunstellig sein<br />
              </span>
            </div>
          </div>
        </div>
        <!-- Zusatz -->
        <div class="input-field">
          <label class="text-base-medium" for="supplement">
            Adresszusatz
          </label>
          <input
            id="supplement"
            name="supplement"
            placeholder="Adresszusatz"
            type="text"
            class="input"
            v-model="currentCustomer.address.supplement"
          />
        </div>
        <!-- PLZ & Ort -->
        <div class="input-group">
          <div class="input-field input-field--fluid">
            <label class="text-base-medium flex-align-center" for="zip">
              <abbr title="Postleitzahl">PLZ</abbr><span class="required" aria-hidden="true">*</span>
            </label>
            <ValidationIndicator
              :dirty="$v.currentCustomer.address.zip.$dirty"
              :valid="isValid('zip', 'address', 'currentCustomer')"
            >
              <input
                id="zip"
                name="zip"
                placeholder="PLZ"
                type="text"
                class="input"
                v-model.trim="currentCustomer.address.zip"
                required
                @blur="$v.currentCustomer.address.zip.$touch"
                @click="$v.currentCustomer.address.zip.$reset"
                :class="{
                  'input--invalid': $v.currentCustomer.address.zip.$dirty && !isValid('zip', 'address', 'currentCustomer'),
                  'input--valid': $v.currentCustomer.address.zip.$dirty && isValid('zip', 'address', 'currentCustomer')
                }"
                aria-describedby="zip-hint"
                aria-errormessage="zip-error"
              />
            </ValidationIndicator>
            <small id="zip-hint" class="sr-only">Pflichtfeld</small>
            <div
              id="zip-error"
              class="error-feedback"
              v-if="!isValid('zip', 'address', 'currentCustomer') && $v.currentCustomer.address.zip.$dirty"
            >
              <span v-if="$v.currentCustomer.address.zip.required === false"> Bitte gib deine Postleitzahl ein.<br /> </span>
              <span v-if="$v.currentCustomer.address.zip.validZipCode === false">
                Die Postleitzahl darf nur aus Zahlen bestehen und muss fünfstellig sein.<br />
              </span>
            </div>
          </div>

          <div class="input-field input-field--fixed">
            <label class="text-base-medium" for="city"> Ort<span class="required" aria-hidden="true">*</span> </label>
            <ValidationIndicator
              :dirty="$v.currentCustomer.address.city.$dirty"
              :valid="isValid('city', 'address', 'currentCustomer')"
            >
              <input
                id="city"
                name="city"
                placeholder="Ort"
                type="text"
                class="input"
                v-model="currentCustomer.address.city"
                required
                @blur="$v.currentCustomer.address.city.$touch"
                @click="$v.currentCustomer.address.city.$reset"
                :class="{
                  'input--invalid': $v.currentCustomer.address.city.$dirty && !isValid('city', 'address', 'currentCustomer'),
                  'input--valid': $v.currentCustomer.address.city.$dirty && isValid('city', 'address', 'currentCustomer')
                }"
                aria-describedby="city-hint"
                aria-errormessage="city-error"
              />
            </ValidationIndicator>
            <small id="city-hint" class="sr-only">Pflichtfeld</small>
            <div
              id="city-error"
              class="error-feedback"
              v-if="!isValid('city', 'address', 'currentCustomer') && $v.currentCustomer.address.city.$dirty"
            >
              <span v-if="$v.currentCustomer.address.city.required === false"> Bitte gib deinen Wohnort ein.<br /> </span>
              <span v-if="$v.currentCustomer.address.city.validCityString === false">
                Der Ortsname darf nur Buchstaben, Leerzeichen und bestimmte Sonderzeichen enthalten.<br />
              </span>
            </div>
          </div>
        </div>
      </fieldset>

      <!-- Geburtsdatum -->
      <fieldset class="inputs-container">
        <legend class="text-xl visually-hidden">Geburtsdatum</legend>
        <div class="inputs-container">
          <h2 class="visually-hidden">Persönliche Informationen</h2>
          <div class="input-field">
            <label class="text-base-medium" for="birthdate">
              Geburtsdatum
            </label>
            <p id="birthdate" class="text-base">{{ currentCustomer.birthDate }}</p>
          </div>
        </div>
      </fieldset>

      <fieldset class="inputs-container">
        <legend class="text-xl visually-hidden">E-Mail-Adresse</legend>
        <div class="inputs-container">
          <h2 class="visually-hidden">Kontaktdaten</h2>
          <div class="input-field">
            <label class="text-base-medium" for="email">
              E-Mail-Adresse<span class="required" aria-hidden="true">*</span>
            </label>
            <ValidationIndicator :dirty="$v.currentCustomer.email.$dirty" :valid="isValid('email', 'currentCustomer')">
              <input
                id="email"
                name="email"
                placeholder="z.B. info@mailadresse.de"
                type="email"
                class="input"
                required
                @blur="$v.currentCustomer.email.$touch"
                @click="$v.currentCustomer.email.$reset"
                :class="{
                  'input--invalid': $v.currentCustomer.email.$dirty && !isValid('email', 'currentCustomer'),
                  'input--valid': $v.currentCustomer.email.$dirty && isValid('email', 'currentCustomer')
                }"
                v-model="currentCustomer.email"
                aria-describedby="email-hint"
                aria-errormessage="email-error"
              />
            </ValidationIndicator>
            <small id="email-hint" class="sr-only">Pflichtfeld</small>
            <div
              id="email-error"
              class="error-feedback"
              v-if="!isValid('email', 'currentCustomer') && $v.currentCustomer.email.$dirty"
            >
              <span v-if="$v.currentCustomer.email.required === false"> Bitte gib deine E-Mail-Adresse ein.<br /> </span>
              <span v-if="$v.currentCustomer.email.email === false">
                Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de).<br />
              </span>
            </div>
          </div>

          <div class="input-field">
            <label class="text-base-medium" for="email-repeat">
              E-Mail-Adresse wiederholen<span class="required" aria-hidden="true">*</span>
            </label>
            <ValidationIndicator :dirty="$v.emailRepeat.$dirty" :valid="isValid('emailRepeat')">
              <input
                id="email-repeat"
                name="email-repeat"
                placeholder="z.B. info@mailadresse.de"
                type="email"
                class="input"
                required
                @blur="$v.emailRepeat.$touch"
                @click="$v.emailRepeat.$reset"
                :class="{
                  'input--invalid': $v.emailRepeat.$dirty && !isValid('emailRepeat'),
                  'input--valid': $v.emailRepeat.$dirty && isValid('emailRepeat')
                }"
                v-model="emailRepeat"
                aria-describedby="email-repeat-hint"
                aria-errormessage="email-repeat-error"
              />
            </ValidationIndicator>
            <small id="email-repeat-hint" class="sr-only">Pflichtfeld</small>
            <div id="email-repeat-error" class="error-feedback" v-if="!isValid('emailRepeat') && $v.emailRepeat.$dirty">
              <span v-if="$v.emailRepeat.required === false"> Bitte wiederhole deine E-Mail-Adresse.<br /> </span>
              <span v-if="$v.emailRepeat.email === false">
                Bitte gib eine gültige E-Mail-Adresse ein (z.B. name@beispiel.de).<br />
              </span>
              <span v-if="$v.emailRepeat.sameAsEmail === false">
                Die eingegebenen E-Mail-Adressen stimmen nicht überein.<br />
              </span>
            </div>
            <small class="text-sm">
              Du erhälst an die eingegebene E-Mail-Adresse eine E-Mail mit einem Bestätigungslink.
            </small>
          </div>

          <!-- this needs a whole rework -->
          <div class="verification" v-if="activateMailVerification">
            <SendVerificationMail :success-message="mailSuccessMessage" :link="mailLink" :show-forced="mailShowForced">
              <template v-for="slot in possibleChildSlotsFor('Verification')" v-slot:[`${slot.child}`]>
                <slot :name="`${slot.parent}`"></slot>
              </template>
            </SendVerificationMail>
          </div>

          <div class="input-field">
            <label class="text-base-medium" for="alternative-phone">
              Alternative Telefonnummer
            </label>
            <ValidationIndicator
              :dirty="$v.currentCustomer.alternativePhone.$dirty"
              :valid="isValid('alternativePhone', 'currentCustomer')"
            >
              <input
                id="alternativePhone"
                name="alternativePhone"
                placeholder="Rufnummer hier eingeben"
                type="tel"
                class="input"
                @blur="$v.currentCustomer.alternativePhone.$touch"
                @click="$v.currentCustomer.alternativePhone.$reset"
                v-model="currentCustomer.alternativePhone"
              />
            </ValidationIndicator>
          </div>
        </div>
      </fieldset>

      <div class="button-wrapper">
        <button :disabled="loading" @click="discardData()" class="button button--secondary" type="reset">
          Abbrechen
        </button>
        <button :disabled="loading" @click="saveData()" class="button button--primary" type="button">
          Speichern
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import isValid from '@/mixins/isValid'
import slotProxy from '@/mixins/slotProxy'
import syncProperty from '@/mixins/syncProperty'
import { required, sameAs, email, alphaNum, maxLength } from 'vuelidate/lib/validators'
import { validZipCode, validCityString } from '@/validators/address'
import { validDate, validDateFormat, validGivenAge } from '@/validators/date'
import ValidationIndicator from '@/components-v2/generic/ValidationIndicator.vue'
import SendVerificationMail from '@/components-v2/flows/LIDL/Activation/EmailVerification/SendVerificationMail.vue'

const alphaSpaces = v => {
  if (!v) return false
  return /^([a-zA-Z -])*$/.test(v.replace(/\u200e/g, ''))
}

export default {
  name: 'ChangePersonalData',
  data() {
    return {
      backup: {}
    }
  },
  mounted() {
    // not happy with this, destroys reference between objects, is needed to "go back to old values"
    this.backup = JSON.parse(JSON.stringify(this.currentCustomer_))
  },
  components: {
    SendVerificationMail,
    ValidationIndicator
  },
  methods: {
    saveData() {
      // debounce button
      if (this.loading) return

      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$nextTick(() => {
          const firstError = document.querySelector('.input--invalid')
          const checkboxError = document.querySelector('.checkbox-class')
          if (firstError) firstError.focus()
          if (checkboxError && !firstError) checkboxError.focus()
        })
      } else {
        this.$emit('save')
      }
    },
    discardData() {
      this.$emit('discard', this.backup)
    }
  },
  watch: {},
  mixins: [
    validationMixin,
    isValid,
    slotProxy(),
    syncProperty('currentCustomer', 'currentCustomer_'),
    syncProperty('emailRepeat', 'emailRepeat_')
  ],
  computed: {
    invalid() {
      if (this.activatePhone) {
        return this.invalidPhone || this.invalidPersonal || this.emailInvalid
      } else {
        return this.invalidPersonal
      }
    }
  },
  props: {
    activatePhone: {
      type: Boolean,
      default: false
    },
    activateMailVerification: {
      type: Boolean,
      default: true
    },
    mailSuccessMessage: {
      type: Boolean,
      default: false
    },
    mailLink: {
      type: String,
      required: true
    },
    mailShowForced: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  validations() {
    const vm = this
    return {
      currentCustomer: {
        salutation: {
          required
        },
        firstName: {
          required,
          alphaSpaces
        },
        lastName: {
          required,
          alphaSpaces
        },
        birthDate: {
          validDate,
          validDateFormat,
          minAge(v) {
            return validGivenAge(v, vm.alowedMinAge)
          },
          birthDateYoungTariffValidation: this.$config?.validation?.validateYoungTariffBirthdate
            ? this.$config?.validation?.validateYoungTariffBirthdate(vm)
            : birthdate => true,
          required
        },
        address: {
          street: {
            required
          },
          houseNumber: {
            required,
            alphaNum,
            maxLength: maxLength(9)
          },
          zip: {
            validZipCode,
            required
          },
          city: {
            validCityString,
            required
          }
        },
        email: {
          required,
          email
        },
        alternativePhone: {}
      },
      emailRepeat: {
        required,
        email,
        sameAsEmail: sameAs(function() {
          return this.currentCustomer.email
        })
      }
    }
  }
}
</script>
